// client/src/components/AdminCodePromo.js

import React, { useState, useEffect, useCallback, useRef } from 'react';
import '../CSS/Dashboard.css';
import AdminNavbar from './AdminNavbar';
import { db } from '../../firebase';
import api from '../../api';
import {
  collection,
  doc,
  addDoc,
  getDocs,
  getDoc,
  query,
  limit as fsLimit,
  startAfter,
} from "firebase/firestore";
import { getAuth } from "firebase/auth";
import { sanitize } from '../../utils/sanitize';
import { useLocation } from 'react-router-dom';

// ====== Devises ======
const ALLOWED_CURRENCIES = ['EUR', 'USD', 'CAD', 'GBP', 'CHF', 'XOF'];
const normalizeCurrency = (cur) => {
  if (!cur) return 'EUR';
  const s = String(cur).trim().toUpperCase();
  if (s === 'CFA' || s === 'FCFA') return 'XOF';
  return ALLOWED_CURRENCIES.includes(s) ? s : 'EUR';
};

// ===== Paramètres =====
const PAGE_SIZE_AFFILIATES = 25;  // nb d'affiliés par page
const PAGE_SIZE_CODES      = 10;  // nb de codes par page par affilié
const PREFETCH_TOP_N       = 3;   // précharger discrètement les N premiers

const AdminCodePromo = () => {
  // ---- Etat Formulaire ----
  const [percentage, setPercentage] = useState('');
  const [duration, setDuration] = useState('');
  const [customPromoCode, setCustomPromoCode] = useState('');
  const [selectedPseudo, setSelectedPseudo] = useState('');
  const [currency, setCurrency] = useState('EUR');
  const [showForm, setShowForm] = useState(false);

  // ---- Pseudo admin / messages ----
  const [pseudo, setPseudo] = useState('');
  const [message, setMessage] = useState('');

  // ---- Affiliés (pagination) ----
  const [affiliates, setAffiliates] = useState([]); // [{id, pseudo}]
  const [loadingAffiliates, setLoadingAffiliates] = useState(true);
  const [fetchingMoreAffiliates, setFetchingMoreAffiliates] = useState(false);
  const [hasMoreAffiliates, setHasMoreAffiliates] = useState(true);
  const [lastAffDoc, setLastAffDoc] = useState(null);

  // ---- Accordéon ouvert ----
  const [expanded, setExpanded] = useState(() => new Set());

  // ---- Cache codes + pagination par affilié ----
  // codesCacheRef: { [uid]: { loaded, pages: [[...]], lastDoc, hasMore, loading, inFlight?: Promise } }
  const codesCacheRef = useRef({});

  const auth = getAuth();
  const user = auth.currentUser;
  const uid = user ? user.uid : null;
  const location = useLocation();
  const preselectAffiliateId = location.state?.preselectAffiliateId || '';
  const preselectAffiliatePseudo = location.state?.preselectAffiliatePseudo || '';
  const openFormFromState = !!location.state?.openForm;

  // ===== Pseudo utilisateur courant =====
  useEffect(() => {
    if (!uid) return;
    (async () => {
      try {
        const userDoc = await getDoc(doc(db, 'users', uid));
        if (userDoc.exists()) setPseudo(sanitize(userDoc.data().pseudo || 'Anonyme'));
        else setPseudo('Anonyme');
      } catch (e) {
        console.error('Erreur pseudo admin:', sanitize(e.message));
        setPseudo('Anonyme');
      }
    })();
  }, [uid]);

  // ===== Helpers Firestore (sans orderBy => aucun index requis) =====

  const fetchAffiliatesPage = useCallback(async (cursor = null) => {
    const baseRef = collection(db, 'code promo');
    const parts = [fsLimit(PAGE_SIZE_AFFILIATES)];
    if (cursor) parts.push(startAfter(cursor));
    const qy = query(baseRef, ...parts);
    const snap = await getDocs(qy);
    const list = snap.docs.map(d => ({ id: d.id, pseudo: sanitize(d.data()?.pseudo || '') }));
    const last = snap.docs[snap.docs.length - 1] || null;
    return { list, last, more: snap.docs.length === PAGE_SIZE_AFFILIATES };
  }, []);

  // Première page d'affiliés
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoadingAffiliates(true);
      setMessage('');
      try {
        const { list, last, more } = await fetchAffiliatesPage(null);
        if (cancelled) return;
        // inject preselected affiliate if absent
        const maybeAdd = preselectAffiliateId && !list.some(a => a.id === preselectAffiliateId)
          ? [{ id: preselectAffiliateId, pseudo: sanitize(preselectAffiliatePseudo || preselectAffiliateId) }, ...list]
          : list;
        setAffiliates(maybeAdd);
        setLastAffDoc(last);
        setHasMoreAffiliates(more);
      } catch (e) {
        console.error(e);
        if (!cancelled) setMessage("Erreur lors du chargement des affiliates.");
      } finally {
        if (!cancelled) setLoadingAffiliates(false);
      }
    })();
    return () => { cancelled = true; };
  }, [fetchAffiliatesPage, preselectAffiliateId, preselectAffiliatePseudo]);

  // Pré-sélection depuis AdminUsersViewer (ouvre le formulaire avec l'affilié choisi)
  useEffect(() => {
    if (!preselectAffiliateId) return;
    setShowForm(true);
    setSelectedPseudo(preselectAffiliateId);
    setAffiliates(prev => {
      if (prev.some(a => a.id === preselectAffiliateId)) return prev;
      return [{ id: preselectAffiliateId, pseudo: sanitize(preselectAffiliatePseudo || preselectAffiliateId) }, ...prev];
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [preselectAffiliateId, preselectAffiliatePseudo]);

  // Ouvre le formulaire si demandé (même sans préselection)
  useEffect(() => {
    if (openFormFromState) setShowForm(true);
  }, [openFormFromState]);

  const loadMoreAffiliates = async () => {
    if (!hasMoreAffiliates || fetchingMoreAffiliates || !lastAffDoc) return;
    setFetchingMoreAffiliates(true);
    setMessage('');
    try {
      const { list, last, more } = await fetchAffiliatesPage(lastAffDoc);
      setAffiliates(prev => [...prev, ...list]);
      setLastAffDoc(last);
      setHasMoreAffiliates(more);
    } catch (e) {
      console.error(e);
      setMessage("Erreur lors du chargement supplémentaire d'affiliés.");
    } finally {
      setFetchingMoreAffiliates(false);
    }
  };

  // ===== Codes par affilié =====

  const computeUsedCount = (usedTimestamps) => {
    if (!usedTimestamps) return 0;
    if (Array.isArray(usedTimestamps)) return usedTimestamps.length;
    if (typeof usedTimestamps === 'object') return Object.keys(usedTimestamps).length;
    return 0;
  };

  const readOneCodesPage = useCallback(async (ownerUid, cursor = null) => {
    const colRef = collection(db, 'code promo', ownerUid, 'codes');
    const parts = [fsLimit(PAGE_SIZE_CODES)];
    if (cursor) parts.push(startAfter(cursor));
    const qy = query(colRef, ...parts);
    const snap = await getDocs(qy);
    const rows = snap.docs.map(d => {
      const data = d.data();
      return {
        id: d.id,
        uid: ownerUid,
        promoCode: sanitize(data.promoCode || ''),
        percentage: Number(data.percentage) || 0,
        MonthNumber: Number(data.MonthNumber) || 0,
        usedCount: computeUsedCount(data.usedTimestamps) + (Number(data.activationsCount) || 0),
        createdAt: data.createdAt || null,
        currency: normalizeCurrency(data.currency || 'EUR'),
        __snap: d,
      };
    });

    // Tri client (desc createdAt si présent, sinon par id)
    rows.sort((a, b) => {
      const toTs = (v) => v?.seconds ? v.seconds : (v?.toDate?.()?.getTime?.() || v?.getTime?.() || 0);
      const diff = (toTs(b.createdAt) - toTs(a.createdAt));
      if (diff !== 0) return diff;
      return (a.id < b.id ? -1 : a.id > b.id ? 1 : 0);
    });

    const last = snap.docs[snap.docs.length - 1] || null;
    return { rows, last, more: snap.docs.length === PAGE_SIZE_CODES };
  }, []);

  const ensureFirstCodesPage = useCallback(async (ownerUid) => {
    const cache = codesCacheRef.current[ownerUid];
    if (cache?.loaded) return;
    if (cache?.inFlight) { await cache.inFlight; return; }
    const p = (async () => {
      try {
        const { rows, last, more } = await readOneCodesPage(ownerUid, null);
        codesCacheRef.current[ownerUid] = {
          loaded: true,
          pages: [rows],
          lastDoc: last,
          hasMore: more,
          loading: false,
          inFlight: null,
        };
      } catch (e) {
        console.error('Erreur 1re page codes', ownerUid, e);
        codesCacheRef.current[ownerUid] = {
          loaded: true,
          pages: [[]],
          lastDoc: null,
          hasMore: false,
          loading: false,
          inFlight: null,
        };
      }
    })();
    codesCacheRef.current[ownerUid] = { ...(codesCacheRef.current[ownerUid] || {}), inFlight: p, loading: true };
    await p;
  }, [readOneCodesPage]);

  const loadMoreCodes = async (ownerUid) => {
    const cache = codesCacheRef.current[ownerUid];
    if (!cache?.hasMore || cache?.loading) return;
    codesCacheRef.current[ownerUid] = { ...cache, loading: true };
    try {
      const { rows, last, more } = await readOneCodesPage(ownerUid, cache.lastDoc);
      codesCacheRef.current[ownerUid] = {
        ...cache,
        pages: [...(cache.pages || []), rows],
        lastDoc: last,
        hasMore: more,
        loading: false,
      };
      setExpanded(new Set(expanded)); // forcer rerender
    } catch (e) {
      console.error('Erreur loadMore codes', ownerUid, e);
      codesCacheRef.current[ownerUid] = { ...cache, loading: false };
    }
  };

  const toggleExpand = async (ownerUid) => {
    const next = new Set(expanded);
    if (next.has(ownerUid)) {
      next.delete(ownerUid);
      setExpanded(next);
    } else {
      next.add(ownerUid);
      setExpanded(next);
      await ensureFirstCodesPage(ownerUid);
      setExpanded(new Set(next));
    }
  };

  // Préfetch discret pour les N premières lignes
  useEffect(() => {
    if (!affiliates.length) return;
    const ids = affiliates.slice(0, PREFETCH_TOP_N).map(a => a.id);
    const run = () => {
      ids.forEach(async (id) => {
        const c = codesCacheRef.current[id];
        if (expanded.has(id) || c?.loaded || c?.inFlight) return;
        try { await ensureFirstCodesPage(id); } catch {}
      });
    };
    if ('requestIdleCallback' in window) {
      const h = window.requestIdleCallback(run, { timeout: 1200 });
      return () => window.cancelIdleCallback && window.cancelIdleCallback(h);
    } else {
      const t = setTimeout(run, 300);
      return () => clearTimeout(t);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [affiliates]);

  // ===== Génération code promo =====

  const generatePromoCode = () => {
    const lower = 'abcdefghijklmnopqrstuvwxyz';
    const upper = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    const digits = '0123456789';
    const allChars = lower + upper + digits;

    const codeChars = [];
    codeChars.push(lower.charAt(Math.floor(Math.random() * lower.length)));
    codeChars.push(upper.charAt(Math.floor(Math.random() * upper.length)));
    codeChars.push(digits.charAt(Math.floor(Math.random() * digits.length)));
    for (let i = 0; i < 5; i++) {
      codeChars.push(allChars.charAt(Math.floor(Math.random() * allChars.length)));
    }
    for (let i = codeChars.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [codeChars[i], codeChars[j]] = [codeChars[j], codeChars[i]];
    }
    return '#' + codeChars.join('');
  };

  const isPromoCodeUnique = useCallback(async (code) => {
    try {
      const { data } = await api.post('/api/promo-codes/check', { code });
      return !(data?.exists);
    } catch (error) {
      console.error('Erreur vérification unicité code promo :', sanitize(error?.message || ''));
      // On laisse passer pour éviter de bloquer inutilement la création.
      return true;
    }
  }, []);

  const handleGenerate = async () => {
    const safePercentage = sanitize(percentage);
    const safeDuration  = sanitize(duration);
    const safeSelected  = sanitize(selectedPseudo);
    const safeCurrency  = normalizeCurrency(sanitize(currency));

    if (!safePercentage || !safeDuration || !safeSelected) {
      alert("Veuillez renseigner le pourcentage, la durée en mois et sélectionner un affiliate.");
      return;
    }

    const raw = customPromoCode.trim() ? customPromoCode : generatePromoCode();
    const promoCode = sanitize(raw);

    const isUnique = await isPromoCodeUnique(promoCode);
    if (!isUnique) {
      alert("Le code promo existe déjà.");
      return;
    }

    const promoData = {
      promoCode,
      percentage: Number(safePercentage),
      MonthNumber: Number(safeDuration),
      currency: safeCurrency,
      createdAt: new Date()
    };

    try {
      const userDocRef = doc(db, 'code promo', safeSelected);
      const codesCollectionRef = collection(userDocRef, 'codes');
      const addedRef = await addDoc(codesCollectionRef, promoData);

      // injecter dans le cache si l'affilié est déjà chargé
      const current = codesCacheRef.current[safeSelected] || { loaded: true, pages: [] };
      const newItem = {
        id: addedRef.id,
        uid: safeSelected,
        promoCode,
        percentage: Number(safePercentage),
        MonthNumber: Number(safeDuration),
        usedCount: 0,
        createdAt: promoData.createdAt,
        currency: safeCurrency,
      };
      const firstPage = current.pages?.[0] || [];
      const newPages = [[newItem, ...firstPage], ...(current.pages?.slice(1) || [])];
      codesCacheRef.current[safeSelected] = {
        ...current,
        loaded: true,
        pages: newPages,
        lastDoc: current.lastDoc || null,
        hasMore: current.hasMore ?? true,
        loading: false,
      };

      if (expanded.has(safeSelected)) setExpanded(new Set(expanded));

      // reset form
      setPercentage('');
      setDuration('');
      setCustomPromoCode('');
      setSelectedPseudo('');
      setCurrency('EUR');
    } catch (error) {
      console.error("Erreur lors de l'ajout du code promo :", sanitize(error.message));
      alert("Erreur lors de l'ajout du code promo.");
    }
  };

  // ===== UI =====

  const renderCodesTable = (ownerUid) => {
    const state = codesCacheRef.current[ownerUid];
    const rows  = state?.pages?.flat() || [];
    const loading = !!state?.loading;
    const hasMore = !!state?.hasMore;

    if (!state?.loaded && loading) {
      return <div className="card section"><p>Chargement des codes…</p></div>;
    }
    if (state?.loaded && rows.length === 0) {
      return <div className="card section"><p>Aucun code pour cet affiliate.</p></div>;
    }

    return (
      <>
        <div className="table-wrap">
          <table className="pretty-table">
            <thead>
              <tr>
                <th>Code</th>
                <th>Devise</th>
                <th>Pourcentage</th>
                <th>Durée</th>
                <th>Utilisations</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(code => (
                <tr key={`${ownerUid}-${code.id}`}>
                  <td><span className="kbd">{code.promoCode}</span></td>
                  <td><span className="pill">{code.currency}</span></td>
                  <td>{code.percentage ? `${code.percentage}%` : '—'}</td>
                  <td>{code.MonthNumber}</td>
                  <td>{code.usedCount}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {hasMore && (
          <div className="grid-right">
            <button className="btn" onClick={() => loadMoreCodes(ownerUid)} disabled={loading}>
              {loading ? 'Chargement…' : 'Voir plus de codes'}
            </button>
          </div>
        )}
      </>
    );
  };

  return (
    <div className="dashboard-container">
      <AdminNavbar />
      <style>{`
        .acp-page {
          --card: var(--eb-surface);
          --muted: var(--eb-muted);
          --text: var(--eb-text);
          --primary: var(--eb-brand);
          --primary-600: var(--eb-brand-700);
          --border: var(--eb-border);
          width: 100%; max-width: var(--eb-content-max); display: flex; flex-direction: column; gap: 16px;
        }
        .acp-page .eb-page-header { margin-bottom: 0; align-items: center; }
        .dashboard-container .acp-page .eb-page-title { margin: 0; text-align: left; }

        /* ---------- Cards / Sections ---------- */
        .acp-page .card { background: var(--eb-surface); border: 1px solid var(--eb-border); border-radius: var(--eb-radius-lg); box-shadow: var(--eb-shadow-sm); }
        .acp-page .section { padding: 20px; }
        .acp-page .section p { margin: 0; color: var(--eb-muted); font-size: 14px; }

        .acp-page .right { display:flex; align-items:center; gap:10px; flex-wrap: wrap; margin-left: auto; }
        .acp-page .chip { display: inline-flex; align-items: center; padding: 3px 10px; background: var(--eb-surface-2); border:1px solid var(--eb-border); border-radius: var(--eb-radius-pill); font-size: 12.5px; font-weight: 600; color: var(--eb-text-2); white-space: nowrap; }
        .acp-page .btn { display: inline-flex; align-items: center; justify-content: center; gap: 6px; min-height: 40px; background: var(--eb-brand); color: #fff; border: 1px solid transparent; border-radius: var(--eb-radius-sm); padding: 0 14px; font-weight: 600; font-size: 14px; cursor:pointer; box-shadow: var(--eb-shadow-xs); transition: background-color .15s ease; white-space: nowrap; }
        .acp-page .btn:hover:not(:disabled) { background: var(--eb-brand-700); }
        .acp-page .btn:disabled { opacity: .55; cursor:not-allowed; }
        .acp-page .btn.ghost { background: var(--eb-surface); color: var(--eb-text-2); border-color: var(--eb-border-strong); }
        .acp-page .btn.ghost:hover:not(:disabled) { background: var(--eb-surface-2); color: var(--eb-text); }

        /* ---------- Form ---------- */
        .acp-page .form { display:grid; grid-template-columns: repeat(auto-fit, minmax(180px, 1fr)); gap:12px; }
        .acp-page .input, .acp-page .select { width: 100%; min-height: 44px; padding: 0 12px; font-size: 14.5px; border:1px solid var(--eb-border-strong); border-radius: var(--eb-radius-sm); background: var(--eb-surface); }
        .acp-page .input:focus, .acp-page .select:focus { outline: none; border-color: var(--eb-brand-500); box-shadow: var(--eb-ring); }
        .acp-page .acp-form-actions { display:flex; gap:8px; justify-content: center; flex-wrap: wrap; }
        .acp-page .acp-modal-form { grid-template-columns: repeat(2, minmax(0, 1fr)); }
        .acp-page .acp-modal-form > :first-child { grid-column: 1 / -1; }

        /* ---------- Affiliate rows (accordion) ---------- */
        .acp-page .aff-list { display:grid; gap:10px; }
        .acp-page .aff-row { overflow: hidden; }
        .acp-page .aff-head { display:flex; align-items:center; justify-content: space-between; gap: 12px; padding: 14px 18px; flex-wrap: wrap; }
        .acp-page .name { font-weight:600; color: var(--eb-text); font-size: 15px; display: inline-flex; align-items: center; gap: 6px; min-width: 0; overflow-wrap: anywhere; }
        .acp-page .cta-row { display:flex; align-items:center; gap:8px; flex-wrap:wrap; }
        .acp-page .caret { transition: transform .2s ease; display:inline-block; color: var(--eb-subtle); font-size: 18px; line-height: 1; }
        .acp-page .caret.open { transform: rotate(90deg); }
        .acp-page .aff-panel { border-top: 1px solid var(--eb-border); background: var(--eb-surface-2); padding: 16px 18px; }
        .acp-page .aff-panel .card { box-shadow: none; }

        /* ---------- Table ---------- */
        .acp-page .table-wrap { overflow:auto; border:1px solid var(--eb-border); border-radius: var(--eb-radius); background: var(--eb-surface); }
        .acp-page .pretty-table { width: 100%; border-collapse: collapse; min-width: 640px; }
        .acp-page .pretty-table th, .acp-page .pretty-table td { border-bottom:1px solid var(--eb-border); padding: 11px 14px; text-align: left; font-size: 14px; color: var(--eb-text-2); }
        .acp-page .pretty-table tbody tr:last-child td { border-bottom: 0; }
        .acp-page .pretty-table tbody tr:hover { background: var(--eb-brand-50); }
        .acp-page .pretty-table thead th { background: var(--eb-surface-2); font-weight: 600; font-size: 12.5px; color: var(--eb-muted); text-transform: uppercase; letter-spacing: .04em; }
        .acp-page .kbd { font-family: var(--eb-font-mono); font-size: 13px; font-weight: 600; color: var(--eb-text); background: var(--eb-surface-2); padding: 3px 8px; border-radius: 6px; border:1px solid var(--eb-border); display:inline-block; }
        .acp-page .pill { display:inline-block; background: var(--eb-brand-50); color: var(--eb-brand); padding: 3px 10px; border-radius: var(--eb-radius-pill); border: 1px solid var(--eb-brand-100); font-size: 12.5px; font-weight: 600; }

        /* ---------- Grid helpers ---------- */
        .acp-page .grid-right { display:flex; justify-content:flex-end; padding: 12px 0 0; }

        /* ---------- Empty / Skeleton ---------- */
        .acp-page .skeleton { background: linear-gradient(90deg, var(--eb-surface-2) 25%, var(--eb-border) 37%, var(--eb-surface-2) 63%); background-size:400% 100%; animation: acp-shimmer 1.4s infinite; border-radius: var(--eb-radius-sm); }
        @keyframes acp-shimmer { 0%{background-position:100% 0} 100%{background-position:-100% 0} }
        .acp-page .s-row { height: 56px; margin: 8px 18px 16px; }

        .acp-page .hint { color: var(--eb-muted); font-size: 13.5px; }

        @media (max-width: 600px) {
          .acp-page .aff-head { flex-direction: column; align-items: stretch; padding: 14px; }
          .acp-page .cta-row .btn { flex: 1 1 auto; }
          .acp-page .acp-form-actions .btn { flex: 1 1 100%; }
          .acp-page .right, .acp-page .right .btn { width: 100%; }
          .acp-page .acp-modal-form { grid-template-columns: minmax(0, 1fr); }
          .acp-page .aff-panel { padding: 12px; }
        }
      `}</style>

      <div className="acp-page">
      <header className="eb-page-header">
        <div>
          <h1 className="eb-page-title">Codes promo</h1>
          <p className="eb-page-subtitle">Gestion par affilié, chargement à la demande</p>
        </div>
        <div className="right">
          <span className="chip">Connecté : {pseudo || '—'}</span>
          {!showForm && (
            <button className="btn" onClick={() => setShowForm(true)}>Nouveau code promo</button>
          )}
        </div>
      </header>

      {/* Formulaire (card) */}
      {showForm && (
        <div className="eb-modal-overlay" onClick={() => setShowForm(false)}>
        <div className="eb-modal" role="dialog" aria-modal="true" aria-labelledby="acp-form-title" onClick={(e) => e.stopPropagation()}>
          <div className="eb-modal__head">
            <h2 id="acp-form-title" className="eb-modal__title">Nouveau code promo</h2>
            <button type="button" className="eb-modal__close" aria-label="Fermer" onClick={() => setShowForm(false)}>×</button>
          </div>
          <div className="eb-modal__body">
          <div className="form acp-modal-form">
            <select
              value={selectedPseudo}
              onChange={(e) => setSelectedPseudo(sanitize(e.target.value))}
              className="select"
            >
              <option value="">Sélectionnez un affiliate</option>
              {affiliates.map((a) => (
                <option key={a.id} value={a.id}>{a.pseudo || a.id}</option>
              ))}
            </select>

            {/* Sélecteur 6 devises */}
            <select
              value={currency}
              onChange={(e) => setCurrency(normalizeCurrency(e.target.value))}
              className="select"
            >
              {ALLOWED_CURRENCIES.map((c) => (
                <option key={c} value={c}>
                  {c === 'XOF' ? 'Devise : Franc CFA (XOF)' : `Devise : ${c}`}
                </option>
              ))}
            </select>

            <input
              type="text"
              placeholder="Code promo (optionnel)"
              value={customPromoCode}
              onChange={(e) => setCustomPromoCode(sanitize(e.target.value))}
              className="input"
            />

            <input
              type="number"
              placeholder="Pourcentage"
              value={percentage}
              onChange={(e) => setPercentage(sanitize(e.target.value))}
              className="input"
            />
            <input
              type="number"
              placeholder="Durée (mois)"
              value={duration}
              onChange={(e) => setDuration(sanitize(e.target.value))}
              min="1"
              className="input"
            />
          </div>

          {message && <div className="eb-alert eb-alert--danger">{message}</div>}
          </div>
          <div className="eb-modal__foot acp-form-actions">
            <button className="btn ghost" onClick={() => setShowForm(false)}>Annuler</button>
            <button className="btn" onClick={handleGenerate}>Générer</button>
          </div>
        </div>
        </div>
      )}

      {message && !showForm && <div className="eb-alert eb-alert--danger">{message}</div>}

      {/* Liste paginée d'affiliés */}
      <div className="aff-list">
        {loadingAffiliates ? (
          <>
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="card aff-row">
                <div className="aff-head">
                  <div className="skeleton" style={{ width: 180, height: 18 }} />
                  <div className="skeleton" style={{ width: 200, height: 36 }} />
                </div>
                <div className="skeleton s-row" />
              </div>
            ))}
          </>
        ) : (
          <>
            {affiliates.length === 0 ? (
              <div className="card section"><p>Aucun affiliate pour le moment.</p></div>
            ) : (
              affiliates.map((a) => {
                const isOpen = expanded.has(a.id);
                const state  = codesCacheRef.current[a.id];
                const count  = state?.pages?.flat()?.length || 0;
                const loadingCodes = !!state?.loading && !state?.loaded;

                return (
                  <div key={a.id} className="card aff-row">
                    <div className="aff-head">
                      <div className="name">
                        <span className={`caret ${isOpen ? 'open' : ''}`}>›</span>&nbsp;{a.pseudo || a.id}
                      </div>
                      <div className="cta-row">
                        {count > 0 && <span className="chip">{count} code{count>1?'s':''}</span>}
                        <button className="btn ghost" onClick={() => { setShowForm(true); setSelectedPseudo(sanitize(a.id)); }}>
                          Ajouter un code
                        </button>
                        <button className="btn" onClick={() => toggleExpand(a.id)}>
                          {isOpen ? 'Masquer' : 'Voir les codes'}
                        </button>
                      </div>
                    </div>

                    {/* Panneau accordéon */}
                    {isOpen && (
                      <div className="aff-panel">
                        {loadingCodes ? (
                          <>
                            <div className="skeleton s-row" />
                            <div className="skeleton s-row" />
                          </>
                        ) : (
                          renderCodesTable(a.id)
                        )}
                      </div>
                    )}
                  </div>
                );
              })
            )}

            {hasMoreAffiliates && (
              <div className="grid-right">
                <button
                  className="btn"
                  onClick={loadMoreAffiliates}
                  disabled={fetchingMoreAffiliates}
                >
                  {fetchingMoreAffiliates ? 'Chargement…' : 'Charger plus d’affiliés'}
                </button>
              </div>
            )}
          </>
        )}
      </div>
      </div>
    </div>
  );
};

export default AdminCodePromo;
